import React, { useEffect, useState } from 'react'
import { ScrollView, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'

import { Screen, CodeInput, Timer, Logo, AuthFooter } from '../components'
import { t } from '../localization'
import { font, useTheme } from '../theme'
import { getNumber } from '../service'
import { usePrevious } from '../hooks/usePrevious'
import config from '../config'
import { AppState } from '../store/redux'
import { authRequestAction } from '../store/redux/auth'
import { PhoneCountry } from '../types'

function CodeConfirmation() {
	const navigation = useNavigation()
	const dispatch = useDispatch()

	const phone = useSelector((state: AppState) => state.auth.phone)
	const fetching = useSelector((state: AppState) => state.auth.fetching)
	const country = useSelector((state: AppState) => state.auth.country)

	const [code, setCode] = useState('')
	const [attemptions, setAttemptions] = useState(1)

	const prevFetching = usePrevious(fetching)

	useEffect(() => {
		if (!phone) {
			navigation.replace('AuthScreen')
		}
	}, [])

	useEffect(() => {
		if (!fetching && !prevFetching && !!code && code.length === config.smsCodeLength) {
			dispatch(authRequestAction({ phone, code }))
		}
	}, [code])

	const onCodeChange = (code: string) => {
		setCode(code)
	}

	const onSendCode = () => {
		setAttemptions(attemptions + 1)
		dispatch(authRequestAction({ phone, call: attemptions < 3 }))
	}

	const isRussia = country?.id === 'ru'

	return (
		<Screen fetching={fetching} hideHeader={true}>
			<ScrollView
				bounces={false}
				contentContainerStyle={styles.contentContainer}
			>
				<Logo style={{ marginTop: 40 }} clickable={false} />
				<View style={styles.wrapper}>
					<Header
						phone={phone}
						attemptions={attemptions}
						isRussia={isRussia}
						country={country}
					/>
					<CodeInput
						style={styles.smsCode}
						smsCodeLength={config.smsCodeLength}
						onCodeEntered={onCodeChange}
					/>
					<CodeRepeat
						onSendCode={onSendCode}
						attemptions={attemptions}
						isRussia={isRussia}
					/>
				</View>
				<AuthFooter />
			</ScrollView>
		</Screen>
	)
}

export default CodeConfirmation

const styles = {
	contentContainer: {
		flexGrow: 1,
		justifyContent: 'space-between',
		alignItems: 'center',
	} as ViewStyle,
	wrapper: {
		width: '33%',
		minWidth: 450,
		paddingHorizontal: 35,
		textAlign: 'center',
		alignItems: 'center',
		marginBottom: 40,
	} as ViewStyle,
	smsCode: {
		marginTop: 16,
	} as TextStyle,
}

function Header({
	phone = '',
	attemptions,
	isRussia,
	country,
}: {
	phone: string,
	attemptions: number,
	isRussia: boolean,
	country: PhoneCountry,
}) {
	const { colors } = useTheme()
	return (
		<View style={{ marginTop: 60 }}>
			<Text style={[headerStyles.auth, { color: colors.text.title }]}>
				{attemptions < 4 || !isRussia ? t('code') : t('sms_code')}
			</Text>
			<Text style={[headerStyles.subtitle, { color: colors.text.secondary }]}>
				{
					attemptions < 4 || !isRussia
						? t('phone_send')
						: t('code_sended_to', [`+${country.code} ${getNumber(phone?.substring(1))}`])
				}
			</Text>
		</View>
	)
}

const headerStyles = StyleSheet.create({
	auth: {
		fontFamily: font(),
		fontSize: 36,
		lineHeight: 29,
		marginBottom: 12,
	},
	subtitle: {
		fontFamily: font(),
		fontSize: 16,
		lineHeight: 19,
	},
})

function CodeRepeat({
	onSendCode,
	attemptions,
	isRussia,
}: {
	onSendCode: () => void,
	attemptions: number,
	isRussia: boolean,
}) {
	const { colors } = useTheme()
	const [canSend, setCanSend] = useState(false)
	const navigation = useNavigation()
	return (
		<View style={smsRepeatStyles.container}>
			{!canSend && (
				<Timer
					duration={110}
					onRender={value => (
						<Text style={[smsRepeatStyles.text, { fontFamily: font(), color: colors.text.secondary }]}>
							{t(attemptions < 4 || !isRussia ? 'recall_code' : 'resend_sms_code').replace('%s', value)}
						</Text>
					)}
					onDone={() => setCanSend(true)}
				/>
			)}
			{canSend && (
				<>
					<Text
						style={[smsRepeatStyles.text, {
							color: colors.text.subtitle,
							marginBottom: 8,
						}]}
						onPress={() => {
							setCanSend(false)
							onSendCode()
						}}
					>
						{attemptions < 4 ? t('send_call') : t('send_code')}
					</Text>
					<Text
						style={[smsRepeatStyles.text, {
							color: colors.text.title,
						}]}
						onPress={() => {
							navigation.navigate('AuthStack', { screen: 'AuthByEmailScreen' })
						}}
					>
						{t('auth_email')}
					</Text>
				</>
			)}
		</View>
	)
}

const smsRepeatStyles = StyleSheet.create({
	container: {
		marginTop: 16,
	},
	text: {
		fontFamily: font(),
		fontSize: 16,
		cursor: 'pointer',
	},
})
